import {
  ArrowClockwise,
  ArrowUpRight,
  ArrowsClockwise,
  ArrowsLeftRight,
  BookOpen,
  Buildings,
  Briefcase,
  Calendar,
  ChartLineUp,
  ChartPie,
  Clock,
  Coins,
  CreditCard,
  CurrencyGbp,
  DotsThreeVertical,
  EnvelopeSimple,
  Export,
  Eye,
  FileText,
  HourglassMedium,
  Notebook,
  PaperPlaneTilt,
  Paperclip,
  Percent,
  Phone,
  Receipt,
  Scales,
  Scroll,
  ShieldCheck,
  UploadSimple,
  User,
  UserCircle,
  UserPlus,
  Users,
  XCircle,
  ArrowSquareOut,
  ArrowsDownUp,
  Bank,
  Bell,
  CalendarBlank,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  ChartBar,
  Check,
  CheckCircle,
  Copy,
  Diamond,
  DotsThree,
  DownloadSimple,
  FlowArrow,
  Folder,
  Funnel,
  GearSix,
  GraduationCap,
  Headset,
  IdentificationBadge,
  Info,
  Kanban,
  Lightning,
  List,
  LockSimple,
  MagnifyingGlass,
  Plus,
  Sparkle,
  SquaresFour,
  Target,
  Tray,
  TrendDown,
  TrendUp,
  UsersThree,
  Warning,
  WarningCircle,
  Wrench,
  X,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';

/**
 * TESSMA ONE iconography — FROZEN (§16).
 *
 * > Library: Phosphor Icons 2.1.1 — `regular` weight for all interface icons,
 * > `fill` reserved for the brand mark. Mixing icon families is prohibited.
 *
 * The dependency is pinned to exactly `@phosphor-icons/react@2.1.1`, so the
 * drawn shapes are the ones the approved design was drawn with.
 *
 * Icons are addressed by the Phosphor kebab-case name the specification uses
 * (`squares-four`, `bank`, `warning-circle`), not by importing a component
 * directly. Two reasons:
 *
 *   1. §16 freezes a *vocabulary*, and a registry is something a reviewer can
 *      read against the specification. A scatter of direct imports is not.
 *   2. It makes "mixing icon families is prohibited" enforceable rather than
 *      aspirational: there is one door, and it only opens onto Phosphor.
 *
 * Sizes are the ones §16 states: 19px navigation, 15–17px KPI and card,
 * 13–15px table and control.
 */

/** Every icon the product may draw. Adding one is a design decision. */
const REGISTRY = {
  /* Navigation vocabulary — frozen per module (§16). */
  'squares-four': SquaresFour,
  bank: Bank,
  'users-three': UsersThree,
  wrench: Wrench,
  headset: Headset,
  'identification-badge': IdentificationBadge,
  kanban: Kanban,
  folder: Folder,
  'graduation-cap': GraduationCap,
  'chart-bar': ChartBar,
  'gear-six': GearSix,

  /* AI vocabulary (§16). */
  sparkle: Sparkle,
  lightning: Lightning,
  'flow-arrow': FlowArrow,
  target: Target,

  /* Semantic (§16). */
  'warning-circle': WarningCircle,
  warning: Warning,
  'check-circle': CheckCircle,
  check: Check,
  info: Info,
  'trend-up': TrendUp,
  'trend-down': TrendDown,

  /* Interface affordances. */
  'caret-up': CaretUp,
  'caret-down': CaretDown,
  'caret-left': CaretLeft,
  'caret-right': CaretRight,
  'arrows-down-up': ArrowsDownUp,
  x: X,
  plus: Plus,
  'dots-three': DotsThree,
  list: List,
  bell: Bell,
  funnel: Funnel,
  'magnifying-glass': MagnifyingGlass,
  'calendar-blank': CalendarBlank,
  'download-simple': DownloadSimple,
  'arrow-square-out': ArrowSquareOut,
  copy: Copy,
  'lock-simple': LockSimple,
  tray: Tray,
  diamond: Diamond,

  /*
   * ---- the senior designer's Phase 1 vocabulary -------------------------
   *
   * Added because the designer's six Phase 1 screens draw them and §1 makes
   * their React implementation the component authority. Still one door and
   * still only Phosphor, so §16's "mixing icon families is prohibited"
   * holds — these are the glyphs their own code imports.
   *
   * TWO ARE SUBSTITUTES, AND THE PINNED VERSION IS WHY. §16 pins
   * @phosphor-icons/react to exactly 2.1.1 so the drawn shapes are the ones
   * the approved design was drawn with. The designer's package is on ^2.1.10
   * and uses two glyphs that 2.1.1 does not export: `Building` and
   * `UserCheck`. Bumping the dependency would redraw every icon in the
   * product to satisfy two, so the nearest 2.1.1 glyphs stand in — `buildings`
   * for the former, `user-circle` for the latter — and the variance is
   * recorded rather than hidden.
   */
  'arrow-clockwise': ArrowClockwise,
  'arrow-up-right': ArrowUpRight,
  'arrows-clockwise': ArrowsClockwise,
  'arrows-left-right': ArrowsLeftRight,
  'book-open': BookOpen,
  buildings: Buildings,
  briefcase: Briefcase,
  calendar: Calendar,
  'chart-line-up': ChartLineUp,
  'chart-pie': ChartPie,
  clock: Clock,
  coins: Coins,
  'credit-card': CreditCard,
  'currency-gbp': CurrencyGbp,
  'dots-three-vertical': DotsThreeVertical,
  'envelope-simple': EnvelopeSimple,
  export: Export,
  eye: Eye,
  'file-text': FileText,
  'hourglass-medium': HourglassMedium,
  notebook: Notebook,
  'paper-plane-tilt': PaperPlaneTilt,
  paperclip: Paperclip,
  percent: Percent,
  phone: Phone,
  receipt: Receipt,
  scales: Scales,
  scroll: Scroll,
  'shield-check': ShieldCheck,
  'upload-simple': UploadSimple,
  user: User,
  'user-circle': UserCircle,
  'user-plus': UserPlus,
  users: Users,
  'x-circle': XCircle,
} as const satisfies Record<string, PhosphorIcon>;

export type IconName = keyof typeof REGISTRY;

export interface IconProps {
  readonly name: IconName;
  /** §16: 19px navigation · 15–17px KPI and card · 13–15px table and control. */
  readonly size?: number;
  /** Defaults to `currentColor`, so an icon inherits the ink of its control. */
  readonly color?: string;
  /**
   * §16 reserves `fill` for the brand mark. Everything else is `regular`, and
   * the type makes the exception explicit rather than leaving it to discipline.
   */
  readonly weight?: 'regular';
  /**
   * An icon beside a label is decoration and is hidden from assistive
   * technology; an icon that *is* the control carries the name instead. Passing
   * a label opts out of `aria-hidden`.
   */
  readonly label?: string;
}

export function Icon({ name, size = 16, color, weight = 'regular', label }: IconProps) {
  const Glyph = REGISTRY[name];
  return (
    <Glyph
      size={size}
      weight={weight}
      {...(color === undefined ? {} : { color })}
      {...(label === undefined ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    />
  );
}

/**
 * The brand mark — the one place §16 permits the `fill` weight. It is a
 * separate component precisely so that the exception cannot leak into ordinary
 * interface icons through a prop.
 */
export function BrandMark({ size = 17 }: { readonly size?: number }) {
  return <Diamond size={size} weight="fill" aria-hidden />;
}

/** The frozen module → icon mapping (§16), so callers cannot invent one. */
export const MODULE_ICONS = {
  dashboard: 'squares-four',
  finance: 'bank',
  crm: 'users-three',
  // Procurement and Compliance & Risk are Phase 1 modules added after v1.0
  // froze this vocabulary. Both reuse a glyph the vocabulary already has
  // rather than adding one: `tray` for the inbound flow Procurement is, and
  // `lock-simple` — the only neutral security glyph here — for Compliance &
  // Risk. `warning-circle` was the stated fallback and is deliberately not
  // used: it is the danger glyph in `feedback`, and a permanent sidebar entry
  // drawn in the error glyph reads as an alert every time the page loads.
  procurement: 'tray',
  compliance: 'lock-simple',
  field: 'wrench',
  service: 'headset',
  hr: 'identification-badge',
  projects: 'kanban',
  documents: 'folder',
  lms: 'graduation-cap',
  ai: 'sparkle',
  reports: 'chart-bar',
  admin: 'gear-six',
} as const satisfies Record<string, IconName>;
